import {
  AccountProductEnum,
  CardNumberChallenge,
  CardNumberResultTypenames,
  CardNumberSuccess,
  CardTypeEnum,
  InternalCheckingAccount,
  Query,
} from '@varo/graphql-schema-types';
import { setStringAsync } from 'expo-clipboard';
import {
  Href,
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import { cardChallengesAtom } from 'features/cardManagement/state/cardChallengeAtom';
import useUpsertFlowStep from 'features/onboarding/hooks/useUpsertFlowStep';
import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FetchCardCvv from 'services/graphql/queries/FetchCardCVV';
import myAccountsQuery from 'services/graphql/queries/MoveMoney/GetMyAccounts';
import DigitalCard from 'shared/components/vxl/DigitalCard/DigitalCard';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { SNACKBAR_DURATION } from 'shared/constants/snackbar';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { toTitleCase } from 'shared/utils/string/string.helpers';
import { useQuery } from 'urql';

import { useFetchCardUserData } from './hooks/useFetchCardUserData';

const HairLineDividerWrapper = () => (
  <View style={styles.hairLineDivider}>
    <HairLineDivider />
  </View>
);

interface MyAccountsResponse {
  myAccounts: {
    internal: InternalCheckingAccount[];
  };
}

const P2PDebitScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'p2pDebit',
  });
  const { submitUpsertFlowStep } = useUpsertFlowStep();

  const {
    onboarding,
    challengeJustCompleted,
    completedChallengeId,
    flowId,
    source,
  } = useLocalSearchParams<{
    onboarding?: string;
    challengeJustCompleted?: string;
    completedChallengeId?: string;
    flowId?: string;
    source?: string;
  }>();
  const pathname = usePathname();
  const { bottom } = useSafeAreaInsets();
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [showAccountSnackBar, setShowAccountSnackBar] = useState(false);

  const { sourceData, fetching, error, cardId } = useFetchCardUserData();

  const [{ data, fetching: loading, error: accountDetailsError }] =
    useQuery<MyAccountsResponse>({
      query: myAccountsQuery,
    });

  const internalAccounts = data?.myAccounts?.internal;
  const checkingCardAccounts = internalAccounts?.find(
    (account) => account.product === AccountProductEnum.Checking,
  );

  const accountNumber =
    (checkingCardAccounts?.fullAccountNumber as string) ?? '';
  const routingNumber = checkingCardAccounts?.routingNumber ?? '';

  const [cvvFetchParams, setCvvFetchParams] = useState<{
    input: { cardId: string | undefined; challengeId?: string };
    includeCvv: boolean;
  } | null>(null);

  const [cardCVVResponse, setCardCVVResponse] = useState<{
    data: CardNumberSuccess | undefined;
    error: boolean;
  }>({ data: undefined, error: false });

  const challengeReturnHandledRef = useRef(false);
  const returnPathRef = useRef(pathname);
  const setCardChallenges = useSetAtom(cardChallengesAtom);

  const [{ data: cvvData, fetching: cvvFetching, error: fetchCardCVVError }] =
    useQuery<Query>({
      query: FetchCardCvv,
      variables: cvvFetchParams ?? { input: { cardId }, includeCvv: true },
      pause: !cvvFetchParams,
      requestPolicy: 'network-only',
    });

  const processCardNumberResult = useCallback(
    (cardNumber: Query['cardNumber']) => {
      setCvvFetchParams(null);
      const challengeBasePath = returnPathRef.current.replace(
        /\/[^/]+$/,
        '/credential-challenge',
      );

      if (
        cardNumber?.__typename === CardNumberResultTypenames.CardNumberSuccess
      ) {
        setCardCVVResponse({
          data: cardNumber as CardNumberSuccess,
          error: false,
        });
      } else if (
        cardNumber?.__typename === CardNumberResultTypenames.CardNumberChallenge
      ) {
        const challenge = cardNumber as CardNumberChallenge;
        if (!challenge.challenges?.length || !cardId) {
          setCardCVVResponse({ data: undefined, error: true });
          return;
        }
        setCardChallenges(challenge.challenges);
        router.push({
          pathname: `${challengeBasePath}/[cardId]/confirm-identity`,
          params: { cardId },
        } as Href);
      } else if (
        cardNumber?.__typename === CardNumberResultTypenames.CardNumberDecline
      ) {
        if (!cardId) {
          setCardCVVResponse({ data: undefined, error: true });
          return;
        }
        router.push({
          pathname: `${challengeBasePath}/[cardId]/declined`,
          params: { cardId },
        } as Href);
      } else {
        setCardCVVResponse({ data: undefined, error: true });
      }
    },
    [cardId, setCardChallenges, router],
  );

  useEffect(() => {
    if (cvvData?.cardNumber) {
      processCardNumberResult(cvvData.cardNumber);
    }
  }, [cvvData, processCardNumberResult]);

  useEffect(() => {
    if (
      challengeJustCompleted !== 'true' ||
      challengeReturnHandledRef.current ||
      !cardId
    ) {
      return;
    }
    challengeReturnHandledRef.current = true;
    setCvvFetchParams({
      input: {
        cardId,
        ...(completedChallengeId && { challengeId: completedChallengeId }),
      },
      includeCvv: true,
    });
  }, [challengeJustCompleted, completedChallengeId, cardId]);

  const handleCopyCardNumberButtonAnalytics = usePressableAnalytics({
    analyticsId: 'copyCardNumberP2P',
    customAttributes: {
      card_type: 'debit_card',
    },
  });

  const handleViewCardButtonAnalytics = usePressableAnalytics({
    analyticsId: 'viewCardClicked',
    customAttributes: {
      card_type: 'debit_card',
    },
  });

  const handleHideCardButtonAnalytics = usePressableAnalytics({
    analyticsId: 'hideCardClicked',
  });

  const handleCopyAccountNumberButtonAnalytics = usePressableAnalytics({
    analyticsId: 'copyAccountNumberP2P',
  });

  const handleCopyRoutingNumberButtonAnalytics = usePressableAnalytics({
    analyticsId: 'copyRoutingNumberP2P',
  });

  useViewDidFocusScreenAnalyticsEvent({
    key: onboarding ? 'P2P Funding Onboarding' : 'P2P Funding Home',
  });

  useAutoHideSnackbar(Boolean(showSnackBar), () => {
    setShowSnackBar(false);
  });

  useAutoHideSnackbar(Boolean(showAccountSnackBar), () => {
    setShowAccountSnackBar(false);
  });

  const showCvvErrorSnackBar =
    (cardCVVResponse.error || !!fetchCardCVVError) && !cvvFetching;

  useAutoHideSnackbar(Boolean(showCvvErrorSnackBar), () => {
    setCardCVVResponse({ data: undefined, error: false });
  });

  useEffect(() => {
    return () => {
      if (flowId) {
        void submitUpsertFlowStep({ input: { id: flowId } });
      }
    };
  }, [flowId, source, submitUpsertFlowStep]);

  const handleFetchCardCvv = () => {
    if (!cardCVVResponse.data) {
      setCardCVVResponse({ data: undefined, error: false });
      setCvvFetchParams({ input: { cardId }, includeCvv: true });
    }
  };

  const handleCopyToClipboard = async () => {
    const cardNumber = cardCVVResponse.data?.pan as string;
    await setStringAsync(cardNumber?.replace(/\s/g, ''));
    setShowSnackBar(true);
  };

  const handleCopiedNumber = async (copiedNumber: string) => {
    await setStringAsync(copiedNumber?.replace(/\s/g, ''));
    setShowAccountSnackBar(true);
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
          headerShown: true,
          headerBackVisible: true,
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching || loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error || !cardId || accountDetailsError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('error')}
              primaryButton={{
                title: translatedText('buttons.okay'),
                onPress: () => router.back(),
              }}
            />
          ) : null
        }
      />
      <ScrollView>
        <View style={styles.info}>
          <TitleLgText>{translatedText('title')}</TitleLgText>
        </View>
        <View style={styles.info}>
          <BodyBaseText>{translatedText('info')}</BodyBaseText>
        </View>
        <DigitalCard
          firstNameText={toTitleCase(
            sourceData?.user?.customerInfo?.name?.first ?? '',
          )}
          lastNameText={toTitleCase(
            sourceData?.user?.customerInfo?.name?.last ?? '',
          )}
          cardNumberText={(cardCVVResponse.data?.pan as string) ?? ''}
          expirationLabel={translatedText('expiration')}
          expirationDateText={
            (cardCVVResponse.data?.expiration as string) ?? ''
          }
          cvvLabel={translatedText('cvv')}
          cvvNumberText={(cardCVVResponse.data?.cvv2 as string) ?? ''}
          cardType={CardTypeEnum.DebitCard}
          canGetInfo={true}
          isSuspended={false}
          showCardInfo={false}
          button={{
            onPress: handleFetchCardCvv,
          }}
          handleCopyToClipboard={async () => {
            await handleCopyToClipboard();
            handleCopyCardNumberButtonAnalytics();
          }}
          cvvFetching={cvvFetching}
          isCardFlipped={(isHidden) => {
            if (isHidden) {
              handleHideCardButtonAnalytics();
            } else {
              handleViewCardButtonAnalytics();
            }
          }}
        />
        <View>
          <ListItem
            primaryCopy={routingNumber ?? ''}
            secondaryCopy={translatedText('routingNumber')}
            lineFormat={ListItemLineFormat.LABEL}
            showsChevron={false}
            analyticsId="routingNumberCopy"
            customAttributes={{}}
            trailingAccessory={{
              id: '1',
              name: VXLComponentId.ICON,
              props: {
                iconKey: 'COPY',
                iconAccessibilityLabel: 'routingNumber-copy-icon',
              },
            }}
            onPress={async () => {
              await handleCopiedNumber(routingNumber);
              handleCopyRoutingNumberButtonAnalytics();
            }}
          />
          <HairLineDividerWrapper />
          <ListItem
            primaryCopy={accountNumber ?? ''}
            secondaryCopy={translatedText('accountNumber')}
            lineFormat={ListItemLineFormat.LABEL}
            showsChevron={false}
            analyticsId="accountNumberCopy"
            customAttributes={{}}
            trailingAccessory={{
              id: '1',
              name: VXLComponentId.ICON,
              props: {
                iconKey: 'COPY',
                iconAccessibilityLabel: 'accountNumber-copy-icon',
              },
            }}
            onPress={async () => {
              await handleCopiedNumber(accountNumber);
              handleCopyAccountNumberButtonAnalytics();
            }}
          />
        </View>
      </ScrollView>
      {showSnackBar ? (
        <Snackbar
          text={translatedText('numberCopied')}
          duration={SNACKBAR_DURATION}
          buttonOnPress={() => setShowSnackBar(false)}
        />
      ) : null}

      {showAccountSnackBar ? (
        <Snackbar
          text={translatedText('copyToClipBoard')}
          duration={SNACKBAR_DURATION}
          buttonOnPress={() => setShowAccountSnackBar(false)}
        />
      ) : null}

      {showCvvErrorSnackBar ? (
        <Snackbar
          text={translatedText('error')}
          duration={SNACKBAR_DURATION}
          buttonOnPress={() =>
            setCardCVVResponse({ data: undefined, error: false })
          }
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  hairLineDivider: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
  info: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
});

export default P2PDebitScreen;
